import { computed, inject, Injectable, signal } from '@angular/core';
import { EnvironmentToken } from './environment.token';

export type Consent = { statistics: boolean; marketing: boolean };
type StoredConsent = Consent & { version: number; date: string };

const STORAGE_KEY = 'lp_consent';
// Bump when categories or vendors change, asks everyone again
const VERSION = 1;
// Cookies of revoked categories, incl. those Meta sets on our domain
const COOKIES: Record<keyof Consent, string[]> = {
  statistics: ['lp_vid'],
  marketing: ['_fbp', '_fbc'],
};

/**
 * Statistics (lp_vid) and marketing (Meta Pixel), stored per browser.
 * - Banner on: opt-in (GDPR)
 * - Banner off: opt-out (Swiss DSG), all on until changed via the footer
 */
@Injectable({ providedIn: 'root' })
export class ConsentService {
  readonly optIn = inject(EnvironmentToken).consentBanner;
  readonly #choice = signal<Consent | null>(this.#read());
  readonly #reopened = signal(false);

  readonly statistics = computed(() => this.#choice()?.statistics ?? !this.optIn);
  readonly marketing = computed(() => this.#choice()?.marketing ?? !this.optIn);
  readonly bannerVisible = computed(() => this.#reopened() || (this.optIn && this.#choice() === null));

  current(): Consent {
    return { statistics: this.statistics(), marketing: this.marketing() };
  }

  reopen(): void {
    this.#reopened.set(true);
  }

  save(consent: Consent): void {
    // Effective state, in opt-out mode that is "on" without a stored choice
    const previous = this.current();
    const revoked = (Object.keys(COOKIES) as (keyof Consent)[]).filter((key) => previous[key] && !consent[key]);

    this.#write(consent);
    this.#choice.set(consent);
    this.#reopened.set(false);

    // A loaded pixel can't be unloaded: drop its cookies and start clean
    if (revoked.length) {
      revoked.forEach((key) => COOKIES[key].forEach((name) => this.#deleteCookie(name)));
      window.location.reload();
    }
  }

  #read(): Consent | null {
    try {
      const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? 'null') as StoredConsent | null;
      if (stored?.version !== VERSION) return null;
      return { statistics: !!stored.statistics, marketing: !!stored.marketing };
    } catch {
      return null;
    }
  }

  #write(consent: Consent): void {
    try {
      const stored: StoredConsent = { ...consent, version: VERSION, date: new Date().toISOString() };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
    } catch {
      // storage blocked: choice holds for this page view only
    }
  }

  /** Meta sets _fbp on the registrable domain, so try every parent domain. */
  #deleteCookie(name: string): void {
    const parts = window.location.hostname.split('.');
    const domains = [''];
    for (let i = 0; i < parts.length - 1; i++) domains.push(`; domain=.${parts.slice(i).join('.')}`);
    domains.forEach((domain) => (document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/${domain}`));
  }
}
